import { z } from 'zod';
import { relativeUrlPathSchema, semverSchema, sha256Schema } from './primitives.js';
const networkOnlyPrefixSchema = relativeUrlPathSchema.refine((value) => value.endsWith('/'), 'network-only prefixes must end with a slash');
export const cacheArtifactSchema = z
    .strictObject({
    artifact_kind: z.enum(['app_shell', 'catalog', 'graph']),
    url_path: relativeUrlPathSchema,
    content_sha256: sha256Schema,
    immutable: z.literal(true),
    response_visibility: z.literal('public'),
    fetch_credentials: z.enum(['same_origin', 'omit']),
})
    .superRefine((value, context) => {
    if (value.artifact_kind === 'app_shell') {
        if (value.fetch_credentials !== 'same_origin') {
            context.addIssue({
                code: 'custom',
                path: ['fetch_credentials'],
                message: 'app shell fetches use same-origin credentials mode',
            });
        }
    }
    else {
        if (value.fetch_credentials !== 'omit') {
            context.addIssue({
                code: 'custom',
                path: ['fetch_credentials'],
                message: 'catalog and graph fetches must omit credentials',
            });
        }
        if (!value.url_path.includes(value.content_sha256)) {
            context.addIssue({
                code: 'custom',
                path: ['url_path'],
                message: 'catalog and graph paths must be content-addressed by the full SHA-256',
            });
        }
    }
});
export const serviceWorkerCachePolicySchema = z
    .strictObject({
    contract_version: z.literal('service_worker_cache_policy.v1'),
    policy_version: semverSchema,
    service_worker_scope: z.literal('/app/'),
    cache_namespace: z.string().regex(/^dmf-[a-z0-9-]+$/),
    cacheable_artifacts: z.array(cacheArtifactSchema).max(10_000),
    network_only_prefixes: z.array(networkOnlyPrefixSchema).min(5).max(64),
    navigation_fallback: relativeUrlPathSchema,
})
    .superRefine((value, context) => {
    const requiredPrefixes = ['/api/', '/auth/', '/bff/', '/app/_exports/', '/app/_user/'];
    for (const required of requiredPrefixes) {
        if (!value.network_only_prefixes.includes(required)) {
            context.addIssue({
                code: 'custom',
                path: ['network_only_prefixes'],
                message: `network-only policy must include ${required}`,
            });
        }
    }
    if (new Set(value.network_only_prefixes).size !== value.network_only_prefixes.length) {
        context.addIssue({
            code: 'custom',
            path: ['network_only_prefixes'],
            message: 'network-only prefixes must be unique',
        });
    }
    const artifactPaths = value.cacheable_artifacts.map((artifact) => artifact.url_path);
    if (new Set(artifactPaths).size !== artifactPaths.length) {
        context.addIssue({
            code: 'custom',
            path: ['cacheable_artifacts'],
            message: 'each cacheable URL must have exactly one immutable descriptor',
        });
    }
    value.cacheable_artifacts.forEach((artifact, index) => {
        if (!artifact.url_path.startsWith(value.service_worker_scope)) {
            context.addIssue({
                code: 'custom',
                path: ['cacheable_artifacts', index, 'url_path'],
                message: 'cacheable artifacts must stay inside the service worker scope',
            });
        }
        if (value.network_only_prefixes.some((prefix) => pathMatchesPrefix(artifact.url_path, prefix))) {
            context.addIssue({
                code: 'custom',
                path: ['cacheable_artifacts', index, 'url_path'],
                message: 'network-only content cannot be listed as cacheable',
            });
        }
    });
    if (!value.navigation_fallback.startsWith(value.service_worker_scope)) {
        context.addIssue({
            code: 'custom',
            path: ['navigation_fallback'],
            message: 'navigation fallback must stay inside the service worker scope',
        });
    }
    if (!value.cacheable_artifacts.some((artifact) => artifact.artifact_kind === 'app_shell' && artifact.url_path === value.navigation_fallback)) {
        context.addIssue({
            code: 'custom',
            path: ['navigation_fallback'],
            message: 'navigation fallback must be an explicitly pinned app-shell artifact',
        });
    }
});
export const cacheLookupSchema = z.strictObject({
    method: z.string().min(1).max(16),
    url_path: relativeUrlPathSchema,
});
export const cacheDecisionSchema = z.strictObject({
    strategy: z.enum(['cache_first_immutable', 'network_only']),
    artifact_kind: z.enum(['app_shell', 'catalog', 'graph']).nullable(),
    expected_sha256: sha256Schema.nullable(),
    fetch_credentials: z.enum(['same_origin', 'omit']),
});
export function decideCacheStrategy(policyInput, requestInput) {
    const policy = serviceWorkerCachePolicySchema.parse(policyInput);
    const parsedRequest = cacheLookupSchema.safeParse(requestInput);
    if (!parsedRequest.success) {
        return networkOnlyDecision();
    }
    const request = parsedRequest.data;
    const blocked = policy.network_only_prefixes.some((prefix) => pathMatchesPrefix(request.url_path, prefix));
    if (request.method !== 'GET' || blocked) {
        return networkOnlyDecision();
    }
    const artifact = policy.cacheable_artifacts.find((candidate) => candidate.url_path === request.url_path);
    if (!artifact) {
        return networkOnlyDecision();
    }
    return {
        strategy: 'cache_first_immutable',
        artifact_kind: artifact.artifact_kind,
        expected_sha256: artifact.content_sha256,
        fetch_credentials: artifact.fetch_credentials,
    };
}
function pathMatchesPrefix(path, prefix) {
    const prefixRoot = prefix.slice(0, -1);
    return path === prefixRoot || path.startsWith(prefix);
}
function networkOnlyDecision() {
    return {
        strategy: 'network_only',
        artifact_kind: null,
        expected_sha256: null,
        fetch_credentials: 'same_origin',
    };
}
//# sourceMappingURL=cache.js.map